import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import api from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ArticleImage } from "@/components/ArticleImage";
import AllArticlesIndex from "@/components/AllArticlesIndex";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { BLOG } from "@/constants/testIds";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

export default function KnowledgeHub() {
  useSEO({
    title: "Water Knowledge Hub — Plain-English Guides From Our Lab",
    description: "Practical water-testing guides written by the Aware Water Testing lab team. Learn what to test for, what results mean and what to do next.",
    url: `${SEO_DEFAULTS.baseUrl}/knowledge-hub`,
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/articles").then(({ data }) => setArticles(data)).finally(() => setLoading(false));
  }, []);

  const featured = articles[0];
  const rest = articles.slice(1);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 lg:pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={[{ name: "Learn" }]} />
          <div className="max-w-3xl mt-6">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-50 mb-5">
              <BookOpen className="w-3.5 h-3.5 text-cyan-600" />
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-700">Water Knowledge Hub</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
              Plain-English answers about <span className="water-text">your water.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-600">
              Practical guides written by our lab technicians — what to test for, what results mean, and what to do next.
            </p>
            <p className="mt-3 text-base text-slate-600">
              On a rainwater tank? Our{" "}
              <Link
                to="/aware-tank-360"
                data-testid="kh-link-tank-360"
                className="font-semibold text-cyan-700 underline decoration-cyan-300 underline-offset-2 hover:text-cyan-800"
              >
                comprehensive rainwater test
              </Link>{" "}
              covers bacteria, minerals, salts and metals, with sample kits posted Australia-wide.
            </p>
          </div>

          {loading ? (
            <div className="mt-16 text-slate-500">Loading articles...</div>
          ) : (
            <div className="mt-14 space-y-12">
              {/* Featured */}
              {featured && (
                <Link
                  data-testid={BLOG.articleCard(featured.slug)}
                  to={`/knowledge-hub/${featured.slug}`}
                  className="group block rounded-3xl overflow-hidden bg-slate-50 border border-slate-100 hover:shadow-2xl transition-all"
                >
                  <div className="grid lg:grid-cols-2 gap-0">
                    <div className="relative h-72 lg:h-auto min-h-[280px] overflow-hidden">
                      <ArticleImage src={featured.cover} alt={featured.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-600">{featured.category} · Featured</div>
                      <h2 className="font-display mt-3 text-2xl lg:text-3xl font-bold text-slate-900 group-hover:text-cyan-700 transition-colors leading-tight">
                        {featured.title}
                      </h2>
                      <p className="mt-3 text-slate-600 leading-relaxed">{featured.excerpt}</p>
                      <div className="mt-5 flex items-center gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {featured.read_time} min read</span>
                        <span className="inline-flex items-center gap-1 font-semibold text-cyan-600 group-hover:translate-x-1 transition-transform">
                          Read article <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Rest grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((a, i) => (
                  <motion.div
                    key={a.slug}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                  >
                    <Link
                      data-testid={BLOG.articleCard(a.slug)}
                      to={`/knowledge-hub/${a.slug}`}
                      className="group block rounded-2xl overflow-hidden bg-white border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all h-full"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <ArticleImage src={a.cover} alt={a.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      </div>
                      <div className="p-6">
                        <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-cyan-600">{a.category}</div>
                        <h3 className="font-display mt-2 text-lg font-semibold text-slate-900 leading-tight group-hover:text-cyan-700 transition-colors">
                          {a.title}
                        </h3>
                        <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.excerpt}</p>
                        <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-slate-500">
                          <Clock className="w-3 h-3" /> {a.read_time} min
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Sep 2026 — the hub's own cards link `/knowledge-hub/:slug`
              resources; this block adds a crawlable index of the blog
              guides so no article depends on "Load more" on /blog. */}
          <AllArticlesIndex
            heading="All water testing guides"
            blurb="Every article from our blog, grouped by topic."
            testid="kh-all-articles"
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
